import { createSignal, Show } from "solid-js";
import { Check, Trash2 } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import type { ShoppingItem } from "@mealplan/powersync";

import { Checkbox } from "@/components/ui/checkbox";
import { useI18n } from "@/providers/i18n-provider";
import { useShoppingItemSwipe } from "../hooks/use-shopping-item-swipe";

interface ShoppingItemRowProps {
  item: ShoppingItem;
  onToggle: (item: ShoppingItem, checked: boolean) => void;
  onDelete: (id: string) => void;
}

/**
 * A single shopping list row with swipe-to-action: swipe right to toggle
 * "bought", swipe left to delete. The checkbox and delete button remain the
 * keyboard / click path; the gesture layers premium touch behaviour on top
 * using the shared recognizer (haptics, axis-lock, reduced-motion safe).
 */
export function ShoppingItemRow(props: ShoppingItemRowProps) {
  const { t } = useI18n();
  const [surface, setSurface] = createSignal<HTMLDivElement>();

  const isChecked = (): boolean => props.item.checked === 1;

  const swipe = useShoppingItemSwipe({
    item: () => props.item,
    isChecked,
    onToggle: (item, checked) => props.onToggle(item, checked),
    onDelete: (id) => props.onDelete(id),
    getSurfaceRef: surface,
  });

  const transform = (): string => `translate3d(${swipe.tx()}px, 0, 0)`;
  const transition = (): string =>
    // oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS values, not UI copy
    swipe.isAnimating() ? "transform 240ms ease-out" : "none";

  return (
    <li class="relative overflow-hidden rounded-xl" data-testid="shopping-item">
      {/* Action affordances revealed beneath the sliding surface. */}
      <div class="pointer-events-none absolute inset-0 flex items-center justify-between px-4">
        <span class="flex items-center gap-1 text-sm font-medium text-success">
          <Check class="h-4 w-4" />
          {t(msg`Bought`)}
        </span>
        <span class="flex items-center gap-1 text-sm font-medium text-danger">
          <Trash2 class="h-4 w-4" />
          {t(msg`Delete`)}
        </span>
      </div>

      <div
        ref={setSurface}
        class="group flex min-h-[52px] touch-pan-y items-center gap-3 border border-border/40 bg-card/50 px-3 py-2 transition-colors data-[checked=true]:bg-success/5"
        style={{ transform: transform(), transition: transition() }}
        data-checked={isChecked()}
        onPointerDown={swipe.handlePointerDown}
        onPointerMove={swipe.handlePointerMove}
        onPointerUp={swipe.handlePointerUp}
        onPointerCancel={swipe.handlePointerCancel}
      >
        <Checkbox
          checked={isChecked()}
          onChange={(checked: boolean) => props.onToggle(props.item, checked)}
        />
        <span
          class="flex-1 text-base"
          classList={{ "line-through text-muted-foreground": isChecked() }}
        >
          {props.item.name}
          <Show when={props.item.quantity !== null && props.item.quantity !== undefined}>
            <span class="text-muted-foreground">
              {" "}
              · {props.item.quantity} {props.item.unit ?? ""}
            </span>
          </Show>
        </span>
        <Show when={props.item.is_manual === 1}>
          <span class="text-[10px] uppercase text-muted-foreground/60">{t(msg`manual`)}</span>
        </Show>
        <button
          type="button"
          class="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground opacity-60 transition-opacity hover:bg-danger/10 hover:text-danger hover:opacity-100 focus-visible:opacity-100 lg:opacity-0 lg:group-hover:opacity-60"
          aria-label={t(msg`Delete item`)}
          onClick={() => props.onDelete(props.item.id)}
        >
          <Trash2 class="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}
